import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Edit2, Printer, Mail, ExternalLink, Camera, Trash2,
  Package, Tag, MapPin, Truck, Hash, Boxes, AlertTriangle, X,
  PackagePlus, History, ArrowDownToLine, ArrowUpFromLine, Hammer, Repeat, ChevronDown, ChevronRight, FileText,
  ClipboardList, Truck as TruckIcon, AlertTriangle as AlertTriangleIcon,
  Globe, ShieldOff,
} from 'lucide-react';
import { openLetterheadWindow } from '../utils/brandedPrint';
import { toast } from 'sonner';
import { csvCell } from '../lib/utils';
import EmailPurchaserDialog from '../components/EmailPurchaserDialog';
import PrintLabelDialog from '../components/PrintLabelDialog';
import RestockDialog from '../components/RestockDialog';
import RestockSessionDialog from '../components/RestockSessionDialog';
import ExpectedOrderDialog from '../components/ExpectedOrderDialog';
import DisposeDialog from '../components/DisposeDialog';
import ItemNotesCard from '../components/ItemNotesCard';
import ItemPricingHistoryCard from '../components/ItemPricingHistoryCard';
import ItemBomImpactCard from '../components/ItemBomImpactCard';
import CostSimulatorCard from '../components/CostSimulatorCard';
import ItemRiskAnalysisCard from '../components/ItemRiskAnalysisCard';
import ItemLotTrackingCard from '../components/ItemLotTrackingCard';
import VendorAlternativesCard from '../components/VendorAlternativesCard';
import { UnitCostText } from '../components/CurrencySelect';
import PriceBreaksCard from '../components/PriceBreaksCard';
import ItemConnectionsCard from '../components/ItemConnectionsCard';

const Field = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />}
    <div className="min-w-0 flex-1">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 break-words">{children}</div>
    </div>
  </div>
);

// iter35: collapsible section wrapper. Persists open/closed state per item+key in localStorage.
const Section = ({ id, itemId, title, defaultOpen = false, headerExtra, children }) => {
  const storageKey = `itemSection:${itemId}:${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = window.localStorage.getItem(storageKey);
      return v === null ? defaultOpen : v === '1';
    } catch { return defaultOpen; }
  });
  const toggle = () => {
    setOpen(o => {
      try { window.localStorage.setItem(storageKey, !o ? '1' : '0'); } catch {}
      return !o;
    });
  };
  return (
    <div className="industrial-card border border-slate-200 rounded-sm bg-white" data-testid={`section-${id}`} data-section-open={open}>
      <button
        type="button"
        onClick={toggle}
        className="w-full px-4 py-3 flex items-center justify-between border-b border-slate-100 hover:bg-slate-50"
        data-testid={`section-toggle-${id}`}
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          {title}
        </div>
        {headerExtra && <div onClick={e => e.stopPropagation()}>{headerExtra}</div>}
      </button>
      {open && <div className="p-0">{children}</div>}
    </div>
  );
};

const ItemDetailPage = () => {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const [item, setItem] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [allVendors, setAllVendors] = useState([]);
  const [location, setLocation] = useState(null);
  const [secondaryLocation, setSecondaryLocation] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printOpen, setPrintOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [restockOpen, setRestockOpen] = useState(false);
  const [disposeOpen, setDisposeOpen] = useState(false);
  // iter40 Phase B
  const [expectedOpen, setExpectedOpen] = useState(false);
  const [restockSessions, setRestockSessions] = useState([]);
  const [sessionViewerId, setSessionViewerId] = useState(null);
  const [bumpExpected, setBumpExpected] = useState(0);
  const [uploading, setUploading] = useState(false);

  const fetchItem = async () => {
    setLoading(true);
    try {
      const { data } = await api().get(`/items/${id}`);
      setItem(data);
      let allLocs = [];
      const vr = await api().get('/vendors');
      setAllVendors(vr.data);
      if (data.preferred_vendor_id) setVendor(vr.data.find(v => v.id === data.preferred_vendor_id) || null);
      if (data.location_id || data.secondary_location_id) {
        const r = await api().get('/locations');
        allLocs = r.data || [];
      }
      setLocation(data.location_id ? (allLocs.find(l => l.id === data.location_id) || null) : null);
      setSecondaryLocation(data.secondary_location_id ? (allLocs.find(l => l.id === data.secondary_location_id) || null) : null);
    } catch (e) {
      // iter78v — auto-redirect to the canonical environment when this
      // deployment doesn't have the item (e.g. label printed against preview
      // but scanned on production, or vice versa).
      if (e?.response?.status === 404) {
        const { tryAltEnvRedirect } = await import('../utils/altEnvRedirect');
        const redirected = await tryAltEnvRedirect(api);
        if (redirected) return; // browser is leaving this page
      }
      toast.error('Failed to load item');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await api().get(`/items/${id}/history`);
      setHistoryData(data);
    } catch {
      // silently ignore — history is non-critical
    }
  };

  // iter40 Phase B — restock-session feed for this item
  const fetchRestockSessions = async () => {
    try {
      const { data } = await api().get(`/items/${id}/restock-sessions`);
      setRestockSessions(data || []);
    } catch {
      // silent
    }
  };

  // iter40 Phase C — reverse "BOMs using this item" lookup
  const [usedInBoms, setUsedInBoms] = useState([]);
  const fetchUsedInBoms = async () => {
    try {
      const { data } = await api().get(`/items/${id}/used-in-boms`);
      setUsedInBoms(data?.boms || []);
    } catch {
      // silent
    }
  };

  // Phase C — Public Item Link state
  const [publicLinkBusy, setPublicLinkBusy] = useState(false);
  const issuePublicLink = async () => {
    setPublicLinkBusy(true);
    try {
      await api().post(`/items/${id}/public-link`);
      toast.success('Public link issued');
      fetchItem();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to issue link'); }
    finally { setPublicLinkBusy(false); }
  };
  const revokePublicLink = async () => {
    if (!window.confirm('Revoke the public link? Anyone with the URL will be locked out.')) return;
    setPublicLinkBusy(true);
    try {
      await api().delete(`/items/${id}/public-link`);
      toast.success('Public link revoked');
      fetchItem();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to revoke link'); }
    finally { setPublicLinkBusy(false); }
  };

  useEffect(() => {
    fetchItem();
    fetchHistory();
    fetchRestockSessions();
    fetchUsedInBoms();
    // eslint-disable-next-line
  }, [id, bumpExpected]);

  // Auto-open restock dialog when ?action=restock (e.g. from a scanned QR)
  useEffect(() => {
    if (!item) return;
    const action = searchParams.get('action');
    if (action === 'restock') {
      setRestockOpen(true);
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [item, searchParams]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024 * 10) {
      toast.error('Max image size is 10MB');
      return;
    }
    // iter78w — Reject HEIC up-front; <img> can't decode it in most browsers
    // so the silent failure made phone uploads vanish on Android Chrome and
    // older Safari. iPhone users can switch Camera → Formats → "Most Compatible"
    // or shoot a fresh photo from the picker.
    const name = (file.name || '').toLowerCase();
    if (name.endsWith('.heic') || name.endsWith('.heif') || file.type === 'image/heic' || file.type === 'image/heif') {
      toast.error('iPhone HEIC photos are not supported. On iPhone: Settings → Camera → Formats → Most Compatible, then re-take the photo.');
      return;
    }
    setUploading(true);
    try {
      const dataUri = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            try {
              // Validate the image actually decoded (some camera formats decode to 0x0)
              if (!img.naturalWidth || !img.naturalHeight) {
                reject(new Error('Photo could not be decoded. Try a different photo or take a fresh one.'));
                return;
              }
              const MAX_DIM = 1600;
              let { naturalWidth: w, naturalHeight: h } = img;
              if (w > MAX_DIM || h > MAX_DIM) {
                const scale = MAX_DIM / Math.max(w, h);
                w = Math.round(w * scale);
                h = Math.round(h * scale);
              }
              const canvas = document.createElement('canvas');
              canvas.width = w; canvas.height = h;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, w, h);
              // Iteratively drop quality if the encoded blob is still huge
              let quality = 0.85;
              let out = canvas.toDataURL('image/jpeg', quality);
              while (out.length > 1024 * 1024 * 1.3 && quality > 0.45) {
                quality -= 0.1;
                out = canvas.toDataURL('image/jpeg', quality);
              }
              resolve(out);
            } catch (err) { reject(err); }
          };
          img.onerror = () => reject(new Error('Photo format is not supported by this browser.'));
          img.src = reader.result;
        };
        reader.onerror = () => reject(new Error('Could not read photo file.'));
        reader.readAsDataURL(file);
      });
      // Send full item payload with new image_data
      const payload = {
        ...item,
        item_sku: item.item_sku,
        item_name: item.item_name,
        description: item.description || '',
        item_category: item.item_category,
        item_type: item.item_type,
        base_uom: item.base_uom,
        track_method: item.track_method,
        preferred_vendor_id: item.preferred_vendor_id || null,
        vendor_sku: item.vendor_sku || '',
        product_link: item.product_link || '',
        location_id: item.location_id || null,
        secondary_location_id: item.secondary_location_id || null,
        image_data: dataUri,
        active_status: item.active_status,
        reorder_point: item.reorder_point,
        current_quantity: item.current_quantity,
        lead_time_days: item.lead_time_days || 0,
        tracking_number: item.tracking_number || '',
      };
      await api().put(`/items/${item.id}`, payload);
      toast.success('Photo updated');
      fetchItem();
    } catch (err) {
      // iter78w — surface the actual decode error (HEIC, codec failure, etc.)
      const msg = err?.response?.data?.detail || err?.message || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // iter35: Item Report (printable) + CSV export
  const exportItemReportCsv = (rep) => {
    const lines = [];
    lines.push(['Item Report', rep.item.item_sku, (rep.item.item_name || '').replace(/,/g, ' ')].map(csvCell).join(','));
    lines.push('Generated,' + new Date(rep.generated_at).toLocaleString());
    lines.push('Section,Field,Value');
    lines.push(['Identity', 'SKU', rep.item.item_sku].map(csvCell).join(','));
    lines.push(['Identity', 'Name', (rep.item.item_name || '').replace(/,/g, ' ')].map(csvCell).join(','));
    lines.push(['Identity', 'Category', rep.item.item_category || ''].map(csvCell).join(','));
    lines.push(['Identity', 'Type', rep.item.item_type || ''].map(csvCell).join(','));
    lines.push(['Identity', 'UOM', rep.item.base_uom || ''].map(csvCell).join(','));
    lines.push(['Stock', 'Current', rep.item.current_quantity || 0].map(csvCell).join(','));
    lines.push(['Stock', 'Reorder Point', rep.item.reorder_point || 0].map(csvCell).join(','));
    lines.push(['Stock', 'Lead Time', rep.item.lead_time_days || 0].map(csvCell).join(','));
    lines.push(['Vendor', 'Name', (rep.vendor || {}).vendor_name || ''].map(csvCell).join(','));
    lines.push(['Vendor', 'SKU', rep.item.vendor_sku || ''].map(csvCell).join(','));
    lines.push(['Risk', 'Band', rep.risk.risk_band].map(csvCell).join(','));
    lines.push(['Risk', 'Score', rep.risk.risk_score].map(csvCell).join(','));
    lines.push(['Risk', 'Vendor Risk Idx', rep.risk.vendor_risk_index || 0].map(csvCell).join(','));
    lines.push(['Risk', 'Manual Note', (rep.risk.manual_risk_note || '').replace(/[,\n]/g, ' ')].map(csvCell).join(','));
    lines.push('');
    lines.push('Pricing & Shipping History');
    lines.push('Recorded At,Unit Cost,Shipping Cost,Vendor');
    rep.pricing_history.forEach(p => lines.push([p.recorded_at, p.unit_cost ?? '', p.shipping_cost ?? '', (p.vendor_name || '').replace(/,/g, ' ')].map(csvCell).join(',')));
    lines.push('');
    lines.push('Used in BOMs');
    lines.push('BOM,Variant,Revision,Status,Qty Per Unit');
    rep.boms.forEach(b => lines.push([(b.bom_name || '').replace(/,/g, ' '), b.bom_variant_code || '', b.revision_number || '', b.status || '', b.quantity_per_unit ?? ''].map(csvCell).join(',')));
    lines.push('');
    lines.push('Notes');
    lines.push('Created At,Author,Note');
    rep.notes.forEach(n => lines.push([n.created_at, (n.author_name || n.created_by_name || '').replace(/,/g, ' '), (n.content || n.body || '')].map(csvCell).join(',')));
    lines.push('');
    lines.push('Lot / Tracking History (recent 200)');
    lines.push('Timestamp,Type,Quantity,Lot,Reason,User,Notes');
    rep.transactions.forEach(t => lines.push([t.timestamp || '', t.transaction_type || '', t.quantity ?? '', t.lot_or_serial || '', (t.reason || '').replace(/,/g, ' '), (t.user_name || '').replace(/,/g, ' '), (t.notes || '')].map(csvCell).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `item-report-${rep.item.item_sku}-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const openItemReport = async (mode = 'print') => {
    try {
      const res = await api().get(`/items/${item.id}/full-report`);
      const rep = res.data;
      if (mode === 'csv') { exportItemReportCsv(rep); return; }
      const fmtDate = (s) => s ? new Date(s).toLocaleString() : '—';
      const escape = (s) => String(s ?? '').replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
      const html = `<!doctype html><html><head><title>Item Report — ${escape(rep.item.item_sku)}</title>
        <style>
          @page { size: Letter; margin: 0.6in; }
          body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #111; line-height: 1.4; }
          h1 { font-size: 20px; margin: 0; letter-spacing: -0.02em; }
          h2 { font-size: 11px; margin: 18px 0 6px; text-transform: uppercase; letter-spacing: 0.12em; color: #475569; border-bottom: 1px solid #cbd5e1; padding-bottom: 4px; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0f172a; padding-bottom: 10px; }
          .header .logo { max-height: 50px; max-width: 180px; object-fit: contain; }
          .meta { text-align: right; font-size: 11px; color: #666; }
          .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
          .pill { display: inline-block; padding: 1px 8px; border-radius: 3px; font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; }
          .pill.critical { background:#fee2e2; color:#991b1b; }
          .pill.high { background:#ffedd5; color:#9a3412; }
          .pill.watch { background:#fef3c7; color:#92400e; }
          .pill.healthy { background:#d1fae5; color:#065f46; }
          table { width: 100%; border-collapse: collapse; margin: 4px 0 12px; }
          th, td { padding: 4px 8px; border-bottom: 1px solid #e5e7eb; text-align: left; font-size: 10.5px; }
          th { background: #f1f5f9; font-weight: 600; color: #334155; text-transform: uppercase; letter-spacing: 0.06em; font-size: 9px; }
          .right { text-align: right; }
          .photo { max-width: 200px; max-height: 150px; object-fit: contain; border: 1px solid #e5e7eb; padding: 4px; background: #fff; }
          .note { background: #f8fafc; border-left: 3px solid #94a3b8; padding: 4px 8px; font-size: 10px; margin-top: 4px; }
          .factor { display: flex; justify-content: space-between; padding: 3px 0; font-size: 10px; border-bottom: 1px dashed #e5e7eb; }
          .footer { margin-top: 24px; font-size: 9px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 6px; }
        </style></head><body>
        <div class="header">
          <div><!--LH--></div>
          <div class="meta">
            <div><strong>${escape(rep.item.item_sku)}</strong></div>
            <div>${escape(rep.item.item_name || '')}</div>
            <div>Generated ${fmtDate(rep.generated_at)} by ${escape(rep.generated_by || '')}</div>
          </div>
        </div>

        <div class="grid2" style="margin-top:14px;">
          <div>
            <h2>Identity & Stock</h2>
            <table>
              <tr><th>SKU</th><td>${escape(rep.item.item_sku)}</td></tr>
              <tr><th>Name</th><td>${escape(rep.item.item_name || '')}</td></tr>
              <tr><th>Category</th><td>${escape(rep.item.item_category || '')}</td></tr>
              <tr><th>Type · UOM</th><td>${escape(rep.item.item_type || '')} · ${escape(rep.item.base_uom || '')}</td></tr>
              <tr><th>Track Method</th><td>${escape(rep.item.track_method || '')}</td></tr>
              <tr><th>On Hand</th><td>${rep.item.current_quantity ?? 0} ${escape(rep.item.base_uom || '')}</td></tr>
              <tr><th>Reorder Point</th><td>${rep.item.reorder_point ?? 0}</td></tr>
              <tr><th>Lead Time</th><td>${rep.item.lead_time_days || 0} day(s)</td></tr>
              <tr><th>Vendor</th><td>${escape((rep.vendor || {}).vendor_name || '—')} ${rep.item.vendor_sku ? `(${escape(rep.item.vendor_sku)})` : ''}</td></tr>
              <tr><th>Location</th><td>${escape((rep.location || {}).location_name || '—')}</td></tr>
            </table>
          </div>
          <div>
            <h2>Photo</h2>
            ${rep.item.image_data ? `<img class="photo" src="${rep.item.image_data}" />` : '<div style="color:#94a3b8;font-size:10px;">No photo</div>'}
            <h2>Risk Analysis</h2>
            <div><span class="pill ${rep.risk.risk_band}">${escape(rep.risk.risk_band)}</span> · score <strong>${rep.risk.risk_score}</strong>${rep.risk.is_band_overridden ? ` (computed ${escape(rep.risk.computed_band)} — manually overridden)` : ''}</div>
            ${rep.risk.manual_risk_note ? `<div class="note">${escape(rep.risk.manual_risk_note)}</div>` : ''}
            <div style="margin-top:6px;">
              ${(rep.risk.factors || []).map(f => `<div class="factor"><span>${escape(f.label)} <span style="color:#94a3b8;">${escape(f.detail || '')}</span></span><span style="font-family:monospace;color:#334155;">w ${f.weight}</span></div>`).join('')}
            </div>
          </div>
        </div>

        <h2>Pricing & Shipping History (${rep.pricing_history.length})</h2>
        ${rep.pricing_history.length === 0 ? '<div style="color:#94a3b8;font-size:10px;">No price points yet.</div>' : `
        <table>
          <tr><th>Recorded</th><th>Vendor</th><th class="right">Unit Cost</th><th class="right">Shipping Cost</th></tr>
          ${rep.pricing_history.map(p => `<tr><td>${fmtDate(p.recorded_at)}</td><td>${escape(p.vendor_name || '')}</td><td class="right">${p.unit_cost != null ? '$' + p.unit_cost : '—'}</td><td class="right">${p.shipping_cost != null ? '$' + p.shipping_cost : '—'}</td></tr>`).join('')}
        </table>`}

        <h2>Used in BOMs (${rep.boms.length})</h2>
        ${rep.boms.length === 0 ? '<div style="color:#94a3b8;font-size:10px;">Not currently consumed by any BOM.</div>' : `
        <table>
          <tr><th>BOM</th><th>Variant</th><th>Rev</th><th>Status</th><th class="right">Qty / Unit</th></tr>
          ${rep.boms.map(b => `<tr><td>${escape(b.bom_name || b.id)}</td><td>${escape(b.bom_variant_code || '')}</td><td>${escape(b.revision_number || '')}</td><td>${escape(b.status || '')}</td><td class="right">${b.quantity_per_unit ?? '—'}</td></tr>`).join('')}
        </table>`}

        <h2>Notes (${rep.notes.length})</h2>
        ${rep.notes.length === 0 ? '<div style="color:#94a3b8;font-size:10px;">No operator notes.</div>' : `
        <table>
          <tr><th>When</th><th>Author</th><th>Note</th></tr>
          ${rep.notes.map(n => `<tr><td>${fmtDate(n.created_at)}</td><td>${escape(n.author_name || n.created_by_name || '')}</td><td style="white-space:pre-wrap;">${escape(n.content || n.body || '')}</td></tr>`).join('')}
        </table>`}

        <h2>Lot / Tracking History — last ${rep.transactions.length} of ${rep.transaction_count}</h2>
        ${rep.transactions.length === 0 ? '<div style="color:#94a3b8;font-size:10px;">No transactions recorded.</div>' : `
        <table>
          <tr><th>When</th><th>Type</th><th class="right">Qty</th><th>Lot</th><th>User</th><th>Reason</th></tr>
          ${rep.transactions.map(t => `<tr><td>${fmtDate(t.timestamp)}</td><td>${escape(t.transaction_type || '')}</td><td class="right">${t.quantity ?? ''}</td><td>${escape(t.lot_or_serial || '')}</td><td>${escape(t.user_name || '')}</td><td>${escape(t.reason || '')}</td></tr>`).join('')}
        </table>`}

        ${(rep.active_recalls || []).length > 0 ? `
        <h2 style="color:#991b1b;">⚠ Active Recalls Touching This SKU</h2>
        <table>
          <tr><th>Lot</th><th>Reason</th><th>Issued</th></tr>
          ${rep.active_recalls.map(r => `<tr><td>${escape(r.lot)}</td><td>${escape(r.reason || '')}</td><td>${fmtDate(r.created_at)}</td></tr>`).join('')}
        </table>` : ''}

        <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
        </body></html>`;
      const w = await openLetterheadWindow(html, { features: 'width=900,height=1200' });
      if (!w) { toast.error('Pop-ups blocked. Allow pop-ups to print the report.'); return; }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load full report');
    }
  };

  const handleRemovePhoto = async () => {
    if (!item?.image_data) return;
    if (!window.confirm('Remove this photo?')) return;
    try {
      const payload = {
        ...item,
        item_sku: item.item_sku,
        item_name: item.item_name,
        description: item.description || '',
        item_category: item.item_category,
        item_type: item.item_type,
        base_uom: item.base_uom,
        track_method: item.track_method,
        preferred_vendor_id: item.preferred_vendor_id || null,
        vendor_sku: item.vendor_sku || '',
        product_link: item.product_link || '',
        location_id: item.location_id || null,
        secondary_location_id: item.secondary_location_id || null,
        image_data: null,
        active_status: item.active_status,
        reorder_point: item.reorder_point,
        current_quantity: item.current_quantity,
        lead_time_days: item.lead_time_days || 0,
        tracking_number: item.tracking_number || '',
      };
      await api().put(`/items/${item.id}`, payload);
      toast.success('Photo removed');
      fetchItem();
    } catch {
      toast.error('Failed to remove photo');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96"><div className="spinner" /></div>
    );
  }
  if (!item) {
    return (
      <div className="text-center py-16 text-slate-500" data-testid="item-not-found">
        <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
        Item not found.
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate('/items')} className="rounded-sm">Back to Item Master</Button>
        </div>
      </div>
    );
  }

  const lowStock = (item.current_quantity ?? 0) < (item.reorder_point ?? 0);
  const qrValue = item.barcode_or_qr_value || item.item_sku;

  return (
    <div className="space-y-6 animate-slideIn" data-testid="item-detail-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/items')} className="rounded-sm" data-testid="item-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 truncate" data-testid="item-detail-name">{item.item_name}</h1>
            <p className="text-slate-500 text-sm mono" data-testid="item-detail-sku">{item.item_sku}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {item.item_type !== 'Service' && <Button onClick={() => setRestockOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="item-detail-restock-btn">
            <PackagePlus className="w-4 h-4 mr-2" /> Restock
          </Button>}
          <Button variant="outline" onClick={() => setExpectedOpen(true)} className="rounded-sm text-blue-600 hover:bg-blue-50" data-testid="item-detail-flag-order-btn">
            <ClipboardList className="w-4 h-4 mr-2" /> Flag Order
          </Button>
          <Button variant="outline" onClick={() => setDisposeOpen(true)} className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="item-detail-dispose-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Dispose
          </Button>
          <Button variant="outline" onClick={() => setEmailOpen(true)} className="rounded-sm" data-testid="item-detail-email-btn">
            <Mail className="w-4 h-4 mr-2" /> Email Purchaser
          </Button>
          <Button variant="outline" onClick={() => setPrintOpen(true)} className="rounded-sm" data-testid="item-detail-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print Label
          </Button>
          <Button variant="outline" onClick={() => openItemReport('print')} className="rounded-sm" data-testid="item-detail-report-btn">
            <FileText className="w-4 h-4 mr-2" /> Item Report
          </Button>
          <Button variant="outline" onClick={() => openItemReport('csv')} className="rounded-sm" data-testid="item-detail-report-csv-btn">
            CSV
          </Button>
          <Button onClick={() => navigate(`/items?edit=${item.id}`)} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="item-detail-edit-btn">
            <Edit2 className="w-4 h-4 mr-2" /> Edit
          </Button>
          {/* iter40 Phase C — Public no-login link */}
          {item.public_token && !item.public_token_revoked ? (
            <Button variant="outline" onClick={revokePublicLink} disabled={publicLinkBusy}
              className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="item-detail-revoke-public-btn">
              <ShieldOff className="w-4 h-4 mr-2" /> Revoke Public
            </Button>
          ) : (
            <Button variant="outline" onClick={issuePublicLink} disabled={publicLinkBusy}
              className="rounded-sm text-blue-600 hover:bg-blue-50" data-testid="item-detail-issue-public-btn">
              <Globe className="w-4 h-4 mr-2" /> Issue Public Link
            </Button>
          )}
        </div>
      </div>

      {/* iter40 Phase C — Public link card (visible when active) */}
      {item.public_token && !item.public_token_revoked && (
        <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 flex items-center gap-3 text-sm" data-testid="public-link-card">
          <Globe className="w-5 h-5 text-blue-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold">No-login Public Item Card</div>
            <div className="font-mono text-[11px] text-slate-700 break-all" data-testid="public-link-url">
              {window.location.origin}/public/items/{item.public_token}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/public/items/${item.public_token}`);
            toast.success('Public URL copied');
          }} data-testid="public-link-copy-btn">Copy</Button>
          <Button size="sm" variant="outline" onClick={() => window.open(`/public/items/${item.public_token}`, '_blank')} data-testid="public-link-open-btn">Open</Button>
        </div>
      )}

      {lowStock && (
        <div className="rounded-sm border border-orange-200 bg-orange-50 px-4 py-3 flex items-center gap-2 text-orange-700 text-sm" data-testid="item-detail-low-stock-banner">
          <AlertTriangle className="w-4 h-4" />
          Low stock — {item.current_quantity} {item.base_uom} remaining (reorder at {item.reorder_point}).
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Photo + QR */}
        <Card className="industrial-card lg:col-span-1">
          <CardContent className="p-5 space-y-5">
            {/* Photo */}
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Photo</div>
              {item.image_data ? (
                <div className="relative group" data-testid="item-detail-photo">
                  <img src={item.image_data} alt={item.item_name} className="w-full max-h-72 object-contain bg-slate-50 border border-slate-200 rounded-sm" />
                  <Button
                    variant="outline" size="sm"
                    onClick={handleRemovePhoto}
                    className="absolute top-2 right-2 rounded-sm bg-white/90"
                    data-testid="item-detail-photo-remove"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-slate-200 rounded-sm py-10 flex flex-col items-center text-slate-400 hover:border-orange-400 hover:text-orange-500 transition"
                  data-testid="item-detail-photo-upload-btn"
                >
                  <Camera className="w-8 h-8 mb-1" />
                  <span className="text-sm">{uploading ? 'Uploading…' : 'Click to upload a photo'}</span>
                  <span className="text-xs text-slate-400 mt-1">PNG / JPG / HEIC up to 10MB · auto-compressed</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.heic,.heif"
                onChange={handlePhotoChange}
                className="hidden"
                data-testid="item-detail-photo-input"
              />
              {item.image_data && (
                <Button
                  variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                  className="rounded-sm mt-2 w-full"
                  data-testid="item-detail-photo-replace-btn"
                >
                  <Camera className="w-3 h-3 mr-2" /> Replace photo
                </Button>
              )}
            </div>

            {/* QR */}
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">QR Code</div>
              <div className="flex flex-col items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm p-4">
                <QRCodeSVG value={qrValue} size={160} level="M" includeMargin={false} data-testid="item-detail-qr" />
                <div className="text-xs text-slate-500 text-center break-all mono">{qrValue}</div>
                <p className="text-xs text-slate-400 text-center">Scanning the QR opens this page.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Details */}
        <Card className="industrial-card lg:col-span-2">
          <CardHeader className="border-b border-slate-100 pb-3">
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Package className="w-4 h-4" /> Item Details
            </h2>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <Field icon={Hash} label="SKU" testId="detail-field-sku"><span className="mono">{item.item_sku}</span></Field>
              <Field icon={Tag} label="Category" testId="detail-field-category">{item.item_category}</Field>
              <Field icon={Boxes} label="Type" testId="detail-field-type">{item.item_type}</Field>
              <Field icon={Hash} label="Base UOM" testId="detail-field-uom">{item.base_uom}</Field>
              <Field icon={Hash} label="Track Method" testId="detail-field-track">{item.track_method}</Field>
              <Field icon={Hash} label="Status" testId="detail-field-status">
                <span className={`status-badge ${item.active_status ? 'status-released' : 'status-obsolete'}`}>
                  {item.active_status ? 'Active' : 'Inactive'}
                </span>
              </Field>
              <Field icon={Boxes} label="Current Quantity" testId="detail-field-quantity">
                <span className={lowStock ? 'text-red-600 font-semibold mono' : 'mono'}>
                  {item.current_quantity} {item.base_uom}
                </span>
              </Field>
              <Field icon={Boxes} label="Reorder Point" testId="detail-field-reorder">
                <span className="mono">{item.reorder_point} {item.base_uom}</span>
              </Field>
              <Field icon={Boxes} label="Unit Cost" testId="detail-field-unit-cost"><UnitCostText item={item} testId="detail-unit-cost" /></Field>
              {(item.min_order_qty || item.order_multiple || item.ordering_notes) && (
                <Field icon={Boxes} label="Ordering" testId="detail-field-ordering">
                  <span className="text-sm">{[item.min_order_qty && `MOQ ${item.min_order_qty}`, item.order_multiple && `multiples of ${item.order_multiple}`].filter(Boolean).join(' · ')}</span>
                  {item.ordering_notes && <div className="text-xs text-slate-500 mt-0.5">{item.ordering_notes}</div>}
                </Field>
              )}
              <Field icon={Truck} label="Preferred Vendor" testId="detail-field-vendor">
                {vendor ? (
                  <button onClick={() => navigate('/vendors')} className="text-orange-600 hover:underline">
                    {vendor.vendor_name}
                  </button>
                ) : <span className="text-slate-400">—</span>}
                {item.vendor_sku && <div className="text-xs text-slate-500 mono mt-0.5">Vendor SKU: {item.vendor_sku}</div>}
              </Field>
              <Field icon={MapPin} label="Storage Location" testId="detail-field-location">
                {location ? `${location.location_name} / ${location.bin_id}` : <span className="text-slate-400">—</span>}
                {secondaryLocation && (
                  <div className="text-xs text-slate-500 mt-0.5" data-testid="detail-field-secondary-location">
                    ↳ {secondaryLocation.location_name} / {secondaryLocation.bin_id}
                  </div>
                )}
              </Field>
              <Field icon={Hash} label="Lead Time" testId="detail-field-lead-time">
                <span className="mono">{item.lead_time_days || 0}</span> day{item.lead_time_days === 1 ? '' : 's'}
              </Field>
              <Field icon={Hash} label="Lot / Tracking #" testId="detail-field-tracking-number">
                {item.tracking_number ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/batch-lot-recall?lot=${encodeURIComponent(item.tracking_number)}`)}
                    className="mono text-orange-600 hover:underline"
                    title="Trace this lot for recall"
                    data-testid="detail-tracking-number-link"
                  >
                    {item.tracking_number}
                  </button>
                ) : <span className="text-slate-400">—</span>}
              </Field>
              <Field icon={ExternalLink} label="Product Link" testId="detail-field-product-link">
                {item.product_link ? (
                  <a
                    href={item.product_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-600 hover:underline break-all inline-flex items-center gap-1"
                    data-testid="item-detail-product-link"
                  >
                    {item.product_link} <ExternalLink className="w-3 h-3" />
                  </a>
                ) : <span className="text-slate-400">—</span>}
              </Field>
            </div>
            {item.description && (
              <div className="pt-4 border-t border-slate-100">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Description</div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="detail-field-description">{item.description}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* History timeline (collapsible) */}
      <Section
        id="history" itemId={item.id}
        title={<span className="flex items-center gap-2"><History className="w-4 h-4" /> Product History</span>}
        defaultOpen={false}
        headerExtra={historyData && (
          <div className="text-xs text-slate-500 mono flex gap-3">
            <span data-testid="history-total-received">Received: {historyData.totals.received}</span>
            <span data-testid="history-total-disposed">Disposed: {historyData.totals.disposed}</span>
            <span data-testid="history-total-consumed">Consumed: {historyData.totals.consumed}</span>
          </div>
        )}
      >
        <div data-testid="item-history-card">
        <CardContent className="p-0">
          {!historyData || historyData.timeline.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="history-empty">
              No activity yet. Use <span className="font-medium">Restock</span> to record incoming stock,
              or <span className="font-medium">Dispose</span> to log defects.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {historyData.timeline.map(ev => {
                const meta = (() => {
                  switch (ev.kind) {
                    case 'restock':  return { Icon: ArrowDownToLine, color: 'text-emerald-600 bg-emerald-50', label: 'Restocked' };
                    case 'dispose':  return { Icon: Trash2,           color: 'text-red-600 bg-red-50',        label: 'Disposed' };
                    case 'waste':    return { Icon: AlertTriangle,    color: 'text-amber-600 bg-amber-50',    label: `Waste — ${ev.transaction_type || ''}` };
                    case 'consume':  return { Icon: ArrowUpFromLine,  color: 'text-slate-600 bg-slate-100',   label: 'Consumed' };
                    case 'transfer': return { Icon: Repeat,           color: 'text-blue-600 bg-blue-50',      label: 'Transferred' };
                    case 'build':    return { Icon: Hammer,           color: 'text-purple-600 bg-purple-50',  label: 'Built' };
                    default:         return { Icon: History,          color: 'text-slate-600 bg-slate-100',   label: ev.transaction_type || 'Event' };
                  }
                })();
                const { Icon, color, label } = meta;
                const ts = ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '—';
                return (
                  <li key={ev.id} className="p-4 flex items-start gap-3" data-testid={`history-event-${ev.id}`}>
                    <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="font-medium text-slate-800">{label}</span>
                        <span className="text-sm text-slate-700 mono">
                          {ev.kind === 'dispose' || ev.kind === 'waste' || ev.kind === 'consume' ? '−' : ev.kind === 'restock' ? '+' : ''}{ev.quantity} {ev.uom}
                        </span>
                        {ev.batch_lot && (
                          <button
                            type="button"
                            onClick={() => navigate(`/batch-lot-recall?lot=${encodeURIComponent(ev.batch_lot)}`)}
                            className="text-xs text-slate-700 mono px-1.5 py-0.5 bg-slate-100 hover:bg-orange-100 hover:text-orange-700 rounded-sm cursor-pointer"
                            title="Trace this lot for recall"
                            data-testid="history-batch-lot"
                          >
                            Lot: {ev.batch_lot}
                          </button>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        <span>{ts}</span>
                        <span className="mx-1.5">•</span>
                        <span>by {ev.user_name}</span>
                        {ev.reason && <><span className="mx-1.5">•</span><span className="text-amber-700">Reason: {ev.reason}</span></>}
                      </div>
                      {ev.notes && (
                        <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{ev.notes}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
        </div>
      </Section>

      {/* Risk analysis (single item) — open by default */}
      <ItemRiskAnalysisCard itemId={item.id} />

      <Section id="connections" itemId={item.id} title="Connections & Key Fields" defaultOpen={true}>
        <ItemConnectionsCard itemId={item.id} />
      </Section>

      {/* Pricing & BOM impact (collapsible, default closed) */}
      <Section id="pricing" itemId={item.id} title="Pricing History" defaultOpen={false}>
        <div className="p-4">
          <ItemPricingHistoryCard itemId={item.id} vendorId={item.preferred_vendor_id} />
          <PriceBreaksCard api={api} item={item} vendors={allVendors} />
        </div>
      </Section>
      <Section id="boms" itemId={item.id} title="Used in BOMs" defaultOpen={false}>
        <div className="p-4">
          <ItemBomImpactCard itemId={item.id} />
        </div>
      </Section>

      {/* Lot tracking history (collapsible, default closed) */}
      <Section id="lots" itemId={item.id} title="Lot / Tracking History" defaultOpen={false}>
        <div className="p-4">
          <ItemLotTrackingCard itemId={item.id} baseUom={item.base_uom} />
        </div>
      </Section>

      {/* Vendor substitution recommender (collapsible, default closed) */}
      <Section id="vendor-alts" itemId={item.id} title="Vendor Alternatives" defaultOpen={false}>
        <div className="p-4">
          <VendorAlternativesCard
            itemId={item.id}
            currentVendorId={item.preferred_vendor_id}
            onVendorSwapped={() => fetchItem()}
          />
        </div>
      </Section>

      {/* Cost simulator (collapsible, default closed) */}
      <Section id="cost-sim" itemId={item.id} title="Cost Simulator" defaultOpen={false}>
        <div className="p-4">
          <CostSimulatorCard itemId={item.id} itemName={item.item_name} baseUom={item.base_uom} />
        </div>
      </Section>

      {/* iter40 Phase B — Receiving History (clickable rows → full session detail) */}
      <Section id="receiving-history" itemId={item.id} title={`Receiving History (${restockSessions.length})`} defaultOpen={false}>
        <div className="p-4">
          {restockSessions.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No restock sessions recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="receiving-history-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2 px-2">When</th>
                    <th className="text-left py-2 px-2">Qty</th>
                    <th className="text-left py-2 px-2">Lot</th>
                    <th className="text-left py-2 px-2">Vendor</th>
                    <th className="text-left py-2 px-2">PO</th>
                    <th className="text-left py-2 px-2">Carrier</th>
                    <th className="text-right py-2 px-2">Landed Unit (CAD)</th>
                    <th className="text-center py-2 px-2">Flags</th>
                    <th className="text-right py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {restockSessions.map(s => (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setSessionViewerId(s.id)}
                      data-testid={`rs-row-${s.id}`}>
                      <td className="py-1.5 px-2">{s.received_at ? new Date(s.received_at).toLocaleDateString() : '—'}</td>
                      <td className="py-1.5 px-2 mono">+{s.quantity}</td>
                      <td className="py-1.5 px-2 mono">{s.batch_lot || '—'}</td>
                      <td className="py-1.5 px-2">{s.vendor_name || '—'}</td>
                      <td className="py-1.5 px-2 mono">{s.purchase_order_number || '—'}</td>
                      <td className="py-1.5 px-2">{s.carrier === 'Other' ? (s.carrier_other || 'Other') : (s.carrier || '—')}</td>
                      <td className="py-1.5 px-2 text-right mono">{s.landed_unit_cost_usd ? `$${Number(s.landed_unit_cost_usd).toFixed(4)}` : '—'}</td>
                      <td className="py-1.5 px-2 text-center">
                        {s.was_damaged && <span title="Damaged" className="inline-block w-2 h-2 bg-red-600 rounded-full mr-0.5"></span>}
                        {s.was_delayed && <span title="Delayed" className="inline-block w-2 h-2 bg-amber-500 rounded-full mr-0.5"></span>}
                        {s.was_challenged && <span title="Challenged" className="inline-block w-2 h-2 bg-orange-600 rounded-full"></span>}
                        {!s.was_damaged && !s.was_delayed && !s.was_challenged && <span className="text-emerald-600">✓</span>}
                      </td>
                      <td className="py-1.5 px-2 text-right">
                        <Button size="sm" variant="ghost" className="h-6 text-[10px]"
                          onClick={(e) => { e.stopPropagation(); setSessionViewerId(s.id); }}>
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Section>

      {/* iter40 Phase C — BOMs Using This Item (reverse lookup) */}
      <Section id="used-in-boms" itemId={item.id} title={`BOMs Using This Item (${usedInBoms.length})`} defaultOpen={false}>
        <div className="p-4">
          <div className="overflow-x-auto" data-testid="used-in-boms-table">
            {usedInBoms.length === 0 ? (
              <p className="text-sm text-slate-400 italic" data-testid="used-in-boms-empty">This item is not consumed by any BOM yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2 px-2">BOM</th>
                    <th className="text-left py-2 px-2">Variant</th>
                    <th className="text-left py-2 px-2">Rev</th>
                    <th className="text-left py-2 px-2">Status</th>
                    <th className="text-left py-2 px-2">Lines</th>
                    <th className="text-right py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {usedInBoms.map(b => (
                    <tr key={b.bom_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/boms/view/${b.bom_id}`)}
                      data-testid={`used-in-bom-row-${b.bom_id}`}>
                      <td className="py-1.5 px-2 font-medium">{b.bom_name || '—'}{b.archived && <span className="text-[10px] text-slate-400 ml-1">(archived)</span>}</td>
                      <td className="py-1.5 px-2 mono">{b.bom_variant_code || '—'}</td>
                      <td className="py-1.5 px-2 mono">{b.bom_revision || '—'}</td>
                      <td className="py-1.5 px-2">{b.status || '—'}</td>
                      <td className="py-1.5 px-2 text-[11px]">
                        {b.lines_using_item.map((ln, i) => (
                          <span key={i} className="mono">{ln.quantity_per} {ln.uom}{ln.is_critical && <span className="text-red-500" title="Critical">*</span>}</span>
                        )).reduce((acc, x, i) => acc.length === 0 ? [x] : [...acc, ', ', x], [])}
                      </td>
                      <td className="py-1.5 px-2 text-right">
                        <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={(e) => { e.stopPropagation(); navigate(`/boms/view/${b.bom_id}`); }}>
                          Open BOM
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Section>

      {/* Notes (collapsible, default closed) */}
      <Section id="notes" itemId={item.id} title="Notes" defaultOpen={false}>
        <div className="p-4">
          <ItemNotesCard itemId={item.id} />
        </div>
      </Section>

      <PrintLabelDialog open={printOpen} onOpenChange={setPrintOpen} item={item} />
      <EmailPurchaserDialog open={emailOpen} onOpenChange={setEmailOpen} requestType="low_stock" item={item} />
      <RestockDialog open={restockOpen} onOpenChange={setRestockOpen} item={item} onDone={() => { fetchItem(); fetchHistory(); fetchRestockSessions(); }} />
      <DisposeDialog open={disposeOpen} onOpenChange={setDisposeOpen} item={item} onDone={() => { fetchItem(); fetchHistory(); }} />
      <ExpectedOrderDialog open={expectedOpen} onOpenChange={setExpectedOpen} item={item} onCreated={() => setBumpExpected(x => x + 1)} />
      <RestockSessionDialog open={!!sessionViewerId} onOpenChange={(o) => !o && setSessionViewerId(null)} sessionId={sessionViewerId} />
    </div>
  );
};

export default ItemDetailPage;
